/* import __COLOCATED_TEMPLATE__ from './manage-participants.hbs'; */
/* RESPONSIBLE TEAM: team-channels */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { isValidEmail } from 'embercom/lib/email';
import storage from 'embercom/vendor/intercom/storage';
import Conversation, { type NewConversation } from 'embercom/objects/inbox/conversation';
import { inject as service } from '@ember/service';
import type UserSummary from 'embercom/objects/inbox/user-summary';
import {
  getParticipantChanges,
  latestUpdatedRecipientsListStorageKey,
} from 'embercom/objects/inbox/user-summary';
import type InboxState from 'embercom/services/inbox-state';
import type Session from 'embercom/services/session';
import { Channel } from 'embercom/models/data/inbox/channels';

interface Args {
  conversation: Conversation;
  onClose: () => unknown;
}

interface Signature {
  Args: Args;
}

export default class ManageParticipants extends Component<Signature> {
  MAX_PARTICIPANTS = 10;
  @service declare inboxState: InboxState;
  @tracked currentParticipants: UserSummary[] = this.args.conversation.participantSummaries;

  readonly getParticipantChanges = getParticipantChanges;
  readonly latestUpdatedRecipientsListStorageKey = latestUpdatedRecipientsListStorageKey;

  @service declare session: Session;

  @action addParticipant(participant: UserSummary) {
    this.currentParticipants = [...this.currentParticipants, participant];
  }

  @action removeParticipant(participant: UserSummary) {
    this.currentParticipants = this.currentParticipants.without(participant);
  }

  get hasErrors() {
    return (
      this.currentParticipants.length < 1 ||
      this.participantsWithStatus.any(({ isInvalid }) => isInvalid)
    );
  }

  get participantsWithStatus() {
    let participantStatuses: {
      participant: UserSummary;
      isInvalid: boolean;
    }[] = [];

    this.currentParticipants.forEach((participant) => {
      participantStatuses.push({
        participant,
        isInvalid: this.isInvalid(participant),
      });
    });

    return participantStatuses;
  }

  private isInvalid(userSummary: UserSummary): boolean {
    return userSummary && !userSummary.id && !isValidEmail(userSummary.email);
  }

  get participantsData() {
    let existingParticipants = this.args.conversation.participantSummaries;
    let { addedParticipants, removedParticipants } = this.getParticipantChanges(
      existingParticipants,
      this.currentParticipants,
    );

    let removedParticipantIds = removedParticipants.map((participant) => participant.id);

    let newParticipantEmails: string[] = [];
    let newParticipantIds: string[] = [];
    addedParticipants?.forEach((participant) =>
      participant.isNewUser
        ? newParticipantEmails.push(participant.email!)
        : newParticipantIds.push(participant.id),
    );

    return { removedParticipantIds, newParticipantEmails, newParticipantIds };
  }

  get draftRecipients() {
    return (
      isEmailCurrentChannel(this.args.conversation) &&
      storage.get(
        this.latestUpdatedRecipientsListStorageKey(
          this.session.workspace.id,
          this.args.conversation?.id,
        ),
      )
    );
  }

  get participantsUpdatedWithDraftRecipients() {
    return (
      this.draftRecipients &&
      Object.values(this.participantsData).some(
        (participants_array) => Array.isArray(participants_array) && participants_array.length > 0,
      )
    );
  }

  private clearRecipientsStorage() {
    storage.remove(
      this.latestUpdatedRecipientsListStorageKey(
        this.session.workspace.id,
        this.args.conversation?.id,
      ),
    );
  }

  @action updateParticipants() {
    this.inboxState.updateConversationParticipants(this.args.conversation, this.participantsData);
    if (this.draftRecipients) {
      this.clearRecipientsStorage();
    }
    this.args.onClose();
  }
}

function isEmailCurrentChannel(conversation: Conversation | NewConversation) {
  return conversation instanceof Conversation && conversation?.channel.current === Channel.Email;
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox2::ManageParticipants': typeof ManageParticipants;
    'inbox2/manage-participants': typeof ManageParticipants;
  }
}
